import React, { cloneElement, forwardRef, useState, useCallback } from 'react';
import MaskedInput from 'react-text-mask';
import { InputTextWrap, Input, IconPrependWrapper, InputWrap, Wrapper } from './styled';
import { NumberButtonField } from './components/number-button-field';
import { IndicatorsContainer } from './components/indicators-container';
import { FormLabel } from '../form-label';
import { FormHelperText } from '../form-helper-text';
import { Select } from '../select';
/**
 * @typedef {object} TextInput
 * @property {string} [value]
 * @property {string} [name]
 * @property {boolean} [isLoading]
 * @property {boolean} [disabled]
 * @property {string} [placeholder]
 * @property {ReactNode} [prependInnerIcon]
 * @property {ReactNode} [appendInnerIcon]
 * @property {'info' | 'error' | 'warning' | 'success'} [textType]
 * @property {boolean} [permanentHint]
 * @property {'small' | 'medium' | 'large' } [sizeComponent]
 * @property {'text' | 'number' | 'password'} [type]
 * @property {boolean} [withSelect]
 * @property {boolean} [isClearable]
 * @property {OptionsType<OptionTypeBase>} [options]
 * @property {ValueType<OptionTypeBase, boolean>} [selectValue]
 *
 * @returns {ReactNode} TextInput component
 */
export const TextInput = forwardRef(function TextInput({ id, mask, pipe, showMask, onFocus, onBlur, placeholder, placeholderChar, keepCharPositions, guide, hintType, hint, textType, permanentHint, prependInnerIcon, appendInnerIcon, fixedPadding, ...props }, ref) {
    return mask ? (React.createElement(MaskedInput, { id: id, mask: mask, pipe: pipe, onBlur: onBlur, onFocus: onFocus, showMask: showMask, placeholder: placeholder, placeholderChar: placeholderChar, keepCharPositions: keepCharPositions, guide: guide, render: (ref, props) => (React.createElement(TextInputInner, { ref: ref, id: id, placeholder: placeholder, hint: hint, hintType: hintType, textType: textType, permanentHint: permanentHint, prependInnerIcon: prependInnerIcon, appendInnerIcon: appendInnerIcon, fixedPadding: fixedPadding, ...props })), ...props })) : (React.createElement(TextInputInner, { id: id, placeholder: placeholder, hintType: hintType, permanentHint: permanentHint, hint: hint, textType: textType, prependInnerIcon: prependInnerIcon, appendInnerIcon: appendInnerIcon, fixedPadding: fixedPadding, onBlur: onBlur, onFocus: onFocus, ...props, ref: ref }));
});
const TextInputInner = forwardRef(function TextInput({ onChange, onKeyDown, onBlur, onFocus, isLoading, value, isClearable, sizeComponent = 'medium', type = 'text', onChangeSelect, placeholder, label, disabled, hint, withSelect, options, selectValue, showPickerByFocus, step, max, min, hintType, textType, permanentHint, prependInnerIcon, appendInnerIcon, fixedPadding, required, tooltip, labelTooltip, onClear, ...props }, ref) {
    const [isTouched, setIsTouched] = useState(false);
    const handleChange = (event) => {
        const { value: inputValue } = event.target;
        const isNumber = type === 'number' && inputValue !== '';
        const isNumberOutsideRange = (max && inputValue > max) || (min && inputValue < min);
        if (isNumber && isNumberOutsideRange)
            return;
        onChange(event);
    };
    const handleKeyDown = (event) => {
        // We want to prevent numbers like '2e5'
        if (type === 'number' && (event.key === 'e' || event.key === 'E')) {
            event.preventDefault();
        }
        if (onKeyDown)
            onKeyDown(event);
    };
    const handleOnFocus = (event) => {
        var _a;
        if (showPickerByFocus && ((_a = event.target) === null || _a === void 0 ? void 0 : _a.showPicker)) {
            event.target.showPicker();
        }
        setIsTouched(true);
        onFocus && onFocus(event);
    };
    const handleOnBlur = (event) => {
        setIsTouched(false);
        onBlur && onBlur(event);
    };
    function getOrder(index, count) {
        if (index === 0)
            return 'first';
        if (index === count - 1)
            return 'last';
        return `${index}`;
    }
    const renderSelect = () => {
        return (React.createElement(Select, { options: options, size: sizeComponent, onChange: onChangeSelect, value: selectValue }));
    };
    const onClearButtonClick = useCallback((e) => {
        e.stopPropagation();
        onClear === null || onClear === void 0 ? void 0 : onClear(value);
    }, [onClear]);
    const renderTextInput = (position) => {
        return (React.createElement(InputWrap, { withSelect: withSelect, sizeComponent: sizeComponent, disabled: disabled, ...props },
            React.createElement(InputTextWrap, { "$isTouched": isTouched, disabled: disabled, "$hintType": hintType, "$size": sizeComponent, "$groupPosition": position, "$permanentHint": permanentHint },
                prependInnerIcon && (React.createElement(IconPrependWrapper, { "$disabled": disabled }, prependInnerIcon)),
                React.createElement(Input, { ref: ref, value: value, required: required, onChange: handleChange, onKeyDown: handleKeyDown, onBlur: handleOnBlur, onFocus: handleOnFocus, disabled: disabled, "$bePrependIcon": Boolean(prependInnerIcon), type: type, min: min, max: max, placeholder: placeholder, "$hintType": hintType, "$textType": textType, "$isTouched": isTouched, "$permanentHint": permanentHint, ...props }),
                type === 'number' ? (React.createElement(NumberButtonField, { value: value, size: sizeComponent, disabled: disabled, onChange: onChange, step: step, min: min, max: max })) : (React.createElement(IndicatorsContainer, { isClearable: isClearable, showClearButton: Boolean(value || props.defaultValue), onClear: onClearButtonClick, appendInnerIcon: appendInnerIcon, disabled: disabled, isLoading: isLoading, size: sizeComponent })))));
    };
    const renderTextInputWithSelect = () => {
        const elements = [renderTextInput('first'), renderSelect()];
        return elements.map((item, i) => {
            return cloneElement(item, {
                groupPosition: getOrder(i, 2),
                key: i
            });
        });
    };
    const content = withSelect ? renderTextInputWithSelect() : renderTextInput();
    return (React.createElement(React.Fragment, null,
        label ? (React.createElement(FormLabel, { labelTooltip: tooltip || labelTooltip, required: required }, label)) : null,
        React.createElement(Wrapper, null, content),
        (!isTouched || permanentHint) && (React.createElement(FormHelperText, { fixedPadding: fixedPadding, msgType: hintType }, hint))));
});
